/* eslint-disable unicorn/no-array-reduce */
import { useTranslation } from 'react-i18next'
import {
  useRefinementList,
  type UseRefinementListProps,
} from 'react-instantsearch'
import Skeleton from 'react-loading-skeleton'
import { isEmpty } from 'remeda'
import { useCategoryStore } from '@velasca/hooks/use-category-store'
import { handleize } from '@velasca/utils/handleize'
import { titleCase } from '@velasca/utils/title-case'
import { trackEvent } from '@velasca/utils/track-event'
import { useAlgoliaFilters } from './AlgoliaSearchProvider'
import {
  FacetsContainer,
  FacetsGroup,
  FacetsList,
  FacetsTitle,
  NoResults,
  SkeletonContainer,
  StyledCheckbox,
} from './styles'
import { getDuplicateTitles, getFilteredCategories } from './utils'

interface FacetValuesProps {
  handle: string
  attribute: string
  loading?: boolean
  sortBy?: UseRefinementListProps['sortBy']
  locale?: string
  isOpen?: boolean
  limit?: number
}

export const FacetValues = ({
  handle,
  attribute,
  sortBy,
  loading = false,
  isOpen = false,
  limit = 20,
}: FacetValuesProps) => {
  const { t } = useTranslation('collection-filters')
  const { items, refine } = useRefinementList({ attribute, sortBy, limit })

  const currentCategory = useCategoryStore((state) => state.currentCategory)

  const { getHexColor } = useAlgoliaFilters()

  // Questa funzione mette i facets delle taglie in gruppi.
  // Retrocompatibile nel caso non ci siano gruppi: tutto viene messo nel gruppo "default"
  const groupedItemsByFacetCategory = items.reduce<{
    [key: string]: {
      [key: string]: { isRefined: boolean; value: string }[]
    }
  }>((acc, { isRefined, value }) => {
    const [, type, sizeGroup] = value.split('||')

    const typeSection = sizeGroup ?? 'default'
    const section = type ?? 'default'

    if (acc[typeSection]?.[section] !== undefined) {
      acc[typeSection][section].push({ isRefined, value })
    } else {
      acc[typeSection] = {
        ...acc[typeSection],
        [section]: [{ isRefined, value }],
      }
    }

    return acc
  }, {})

  const groupedItemsByFacetCategoryArray = Object.entries(
    groupedItemsByFacetCategory,
  )

  const isCategoriesFilter = handle === 'categories'

  return (
    <FacetsList isOpen={isOpen}>
      {
        // faccio un check sugli item, se è presente almeno un elemento
        // selezionato, non mostro lo skeleton
        !loading || items.some(({ isRefined }) => isRefined) ? (
          !isEmpty(items) ? (
            groupedItemsByFacetCategoryArray.map(
              ([macroCategoryKey, categories]) => {
                const categoriesArray = Object.entries(categories)

                // controllo che le altre macrocategorie siano vuote,
                // in caso contrario non stampo il titolo
                const areOtherCategoriesEmpty =
                  groupedItemsByFacetCategoryArray.every(
                    ([categoryName, categoryObject]) => {
                      const categoryArray = Object.values(categoryObject)

                      return (
                        categoryName === macroCategoryKey ||
                        (categoryName !== macroCategoryKey &&
                          isEmpty(categoryArray))
                      )
                    },
                  )

                if (isEmpty(categoriesArray)) {
                  return null
                }

                return (
                  <FacetsContainer key={macroCategoryKey}>
                    {macroCategoryKey !== 'default' &&
                      !isEmpty(categoriesArray) &&
                      !areOtherCategoriesEmpty && (
                        <FacetsTitle>
                          {t(`filterType_${macroCategoryKey}`)}
                        </FacetsTitle>
                      )}

                    {categoriesArray.map(([categoryKey, category]) => {
                      if (isEmpty(category)) {
                        return null
                      }

                      const filteredCategories = isCategoriesFilter
                        ? getFilteredCategories(category, currentCategory)
                        : category

                      return (
                        <FacetsGroup key={categoryKey}>
                          {filteredCategories.map(({ isRefined, value }) => {
                            const id = `attribute-${handle}-${handleize(value)}`

                            // Nel caso in cui non ci siano || viene semplicemente restituito il valore
                            let displayValue = value.split('||')[0]

                            if (isCategoriesFilter) {
                              const duplicateTitles = getDuplicateTitles(
                                currentCategory?.children,
                              )

                              const currentCategoryChildren =
                                currentCategory?.children?.find(
                                  (item) => item.handle === value,
                                )

                              if (currentCategoryChildren?.title) {
                                if (
                                  duplicateTitles.includes(
                                    currentCategoryChildren.title,
                                  )
                                ) {
                                  displayValue = titleCase(
                                    currentCategoryChildren.handle,
                                  )
                                } else {
                                  displayValue = currentCategoryChildren.title
                                }
                              }
                            }

                            return (
                              <StyledCheckbox
                                key={value}
                                isChecked={isRefined}
                                className={handle}
                                bulletColor={getHexColor(displayValue)}
                              >
                                <label htmlFor={id}>
                                  <input
                                    id={id}
                                    type="checkbox"
                                    value={value}
                                    checked={isRefined}
                                    onChange={() => {
                                      // il valore di isRefined è precedente all'evento
                                      // che lo cambia. Per cui, se si clicca sull'input
                                      // quando questo non è stato precedentemente selezionato,
                                      // isRefined in questo punto sarà false
                                      !isRefined &&
                                        trackEvent({
                                          googleAnalytics: {
                                            eventName: 'filters_used',
                                            data: { value },
                                          },
                                        })
                                      refine(value)
                                    }}
                                  />
                                  {displayValue}
                                </label>
                              </StyledCheckbox>
                            )
                          })}
                        </FacetsGroup>
                      )
                    })}
                  </FacetsContainer>
                )
              },
            )
          ) : (
            <NoResults>{t('noFiltersAvailable')}</NoResults>
          )
        ) : (
          <FacetsContainer>
            <FacetsGroup>
              {[...Array(9)].map((_, i) => (
                <SkeletonContainer key={`skel-${i}`}>
                  <Skeleton inline width="2.4rem" height="2.4rem" />
                  <Skeleton inline width="14rem" height="1.5rem" />
                </SkeletonContainer>
              ))}
            </FacetsGroup>
          </FacetsContainer>
        )
      }
    </FacetsList>
  )
}
